import { Info, Sparkle, Lock } from '@phosphor-icons/react';
import { useState } from 'react';
import { useDemo } from '../../../../../../utils/DemoContext';

/**
 * DemoDataBadge Component
 *
 * Enterprise-grade badge that indicates demo/preview data is being displayed.
 * Shows helpful context about when real data will be available.
 *
 * Design: Subtle, professional, non-intrusive yet clear
 */

interface DemoDataBadgeProps {
  /** Position of the badge */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  /** Custom message for the tooltip */
  message?: string;
  /** Style variant */
  variant?: 'badge' | 'overlay' | 'inline';
}

const DEFAULT_MESSAGE =
  'This is preview data to help you explore TACH features. Real data will be available once you complete onboarding and your account is approved by our banking partners.';

export function DemoDataBadge({
  position = 'top-right',
  message = DEFAULT_MESSAGE,
  variant = 'badge',
}: DemoDataBadgeProps) {
  const { isDemo } = useDemo();
  const [showTooltip, setShowTooltip] = useState(false);

  if (isDemo) return null;

  const positionClasses = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };

  if (variant === 'inline') {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
        <Sparkle size={12} weight="fill" />
        <span>Demo Data</span>
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      <div className="absolute inset-0 z-10 rounded-lg backdrop-blur-sm">
        <div className="flex h-full w-full items-center justify-center p-6">
          <div className="max-w-md text-center">
            <div className="mb-3 flex justify-center">
              <div className="rounded-full bg-blue-100 p-3">
                <Sparkle size={24} weight="fill" className="text-blue-600" />
              </div>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Preview Data
            </h3>
            <p className="text-sm leading-relaxed text-gray-700">{message}</p>
          </div>
        </div>
      </div>
    );
  }

  // Default: badge variant
  return (
    <div className={`absolute z-10 ${positionClasses[position]}`}>
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {/* Badge */}
        <div className="flex cursor-help items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 px-3 py-1.5 shadow-sm transition-all hover:shadow-md">
          <Sparkle size={14} weight="fill" className="text-white" />
          <span className="text-xs font-semibold text-white">Demo</span>
        </div>

        {/* Tooltip */}
        {showTooltip && (
          <div
            className="absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-lg border border-gray-200 p-4 shadow-xl"
            role="tooltip"
          >
            {/* Arrow */}
            <div className="absolute -top-2 left-1/2 h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t border-gray-200" />

            {/* Content */}
            <div className="relative">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-full bg-blue-50 p-1.5">
                  <Info size={16} className="text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900">Preview Data</h4>
              </div>
              <p className="text-sm leading-relaxed text-gray-700">{message}</p>

              <div className="mt-3 rounded-lg bg-blue-50 p-2.5">
                <p className="text-xs font-medium text-blue-900">
                  💡 Complete your onboarding to unlock real data
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * DemoDataWrapper
 *
 * Wraps widgets with a diagonal "Preview Data" ribbon badge in the top-right corner
 * Subtle, non-intrusive indicator that data is demo/preview
 */
interface DemoDataWrapperProps {
  children: React.ReactNode;
  /** Whether to show the demo badge */
  showBadge?: boolean;
  /** Layout variant - kept for backward compatibility but not used */
  layout?: 'vertical' | 'horizontal';
  /** Additional classes to apply to the wrapper */
  className?: string;
}

export function DemoDataWrapper({
  children,
  showBadge = true,
  layout = 'vertical',
  className = '',
}: DemoDataWrapperProps) {
  const { isDemo } = useDemo();

  if (isDemo || !showBadge) {
    // Still wrap with div to preserve className even when not showing badge
    return <div className={className || 'w-full'}>{children}</div>;
  }

  return (
    <div className={`group relative ${className || 'w-full'}`}>
      {/* Content */}
      {children}

      {/* Diagonal ribbon badge in top-right corner - shows on hover */}
      <div className="pointer-events-none absolute right-0 top-0 z-30 h-[150px] w-[150px] overflow-hidden opacity-0 transition-opacity duration-200 group-hover:opacity-100">
        <div
          className="absolute right-[-50px] top-[30px] flex w-[260px] items-center justify-center gap-1.5 py-1.5 pl-[40px] shadow-md"
          style={{
            background: 'linear-gradient(135deg, #155dfc 0%, #6A9E68 100%)',
            transform: 'rotate(45deg)',
            transformOrigin: 'center center',
            minWidth: '140px',
          }}
        >
          <Lock size={12} weight="fill" className="text-white" />
          <span className="text-xs font-semibold tracking-wide text-white">
            Preview Data
          </span>
        </div>
      </div>
    </div>
  );
}
